/*
 * Mini Rails UJS: A modern approximation of @rails/ujs including only the parts we use.
 * ┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅┅
 * Supports:
 *  ◦ data-confirm="…" on links and buttons
 *  ◦ data-disable-with="…" on links and buttons
 *  ◦ data-method="…" on links
 *  ◦ data-remote="true" on forms
 */
import { createElement, csrfParam, csrfToken, domReady, nextFrame, submitForm } from "../helpers"

// Configuration

const config = {
  initializers: [refreshCsrfTokens],
  eventHandlers: {
    click: {
      "[data-confirm]:is(a,button,[type=submit])": handleConfirm,
      "[data-disable-with]:is(a,button,[type=submit]):not(:disabled)": handleDisableWith,
      "[data-method]:is(a)": handleMethod,
    },
    submit: {
      "[data-remote]:is(form)": handleRemote,
    },
  },
}

// Install initializers

for (const initializer of config.initializers) {
  domReady(initializer)
}

// Install event handlers

for (const [eventName, handlers] of Object.entries(config.eventHandlers)) {
  const handlerEntries = Object.entries(handlers)

  addEventListener(eventName, (event) => {
    if (event.defaultPrevented) return
    if (typeof event.target.closest != "function") return

    for (const [selector, handler] of handlerEntries) {
      const element = event.target.closest(selector)
      if (element) handler({ element, event })
      if (event.defaultPrevented) return
    }
  })
}

// Initializers

function refreshCsrfTokens() {
  const name = csrfParam()
  const value = csrfToken()
  const inputs = document.querySelectorAll(`input[name="${name}"]`)
  for (const input of inputs) input.value = value
}

// Event handlers

function handleConfirm({ element, event }) {
  const message = element.getAttribute("data-confirm")
  if (!confirm(message)) event.preventDefault()
}

async function handleDisableWith({ element }) {
  if (element.form && !element.form.checkValidity()) return
  const html = element.getAttribute("data-disable-with")
  await nextFrame()
  element.innerHTML = html
  element.disabled = true
}

async function handleMethod({ element, event }) {
  event.preventDefault()

  const form = createElement("form", { action: element.href, method: "post", hidden: true })
  form.append(
    createElement("input", { type: "hidden", name: csrfParam(), value: csrfToken() }),
    createElement("input", { type: "hidden", name: "_method", value: element.getAttribute("data-method") })
  )
  document.body.append(form)

  submitForm(form)
  await nextFrame()
  form.remove()
}

async function handleRemote({ element, event }) {
  event.preventDefault()
  const { submitter } = event

  const action = submitter?.getAttribute("formaction") || element.action
  const method = submitter?.getAttribute("formmethod") || element.method

  const body = new FormData(element)
  if (submitter?.name) body.append(submitter.name, submitter.value)

  const headers = { "X-Requested-With": "XMLHttpRequest", "X-CSRF-Token": csrfToken() }

  element.dispatchEvent(new CustomEvent("remote:submit-start", { bubbles: true }))
  const response = await fetch(action, { method, body, headers })
  element.dispatchEvent(new CustomEvent("remote:submit-end", { bubbles: true, detail: { response } }))
}
